import { useEffect, useRef } from 'react';

import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { BottomSheet, Text } from '@myrealtrip/web-ui';

import { DIRECTION } from '~constants/pointToPointTicket';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import usePlaceSearch from '~hooks/pointToPointTicket/usePlaceSearch';
import usePopularPlaceList from '~queries/transportation/pointToPointTicket/usePopularPlaceList';
import usePopularRouteList from '~queries/transportation/pointToPointTicket/usePopularRouteList';

import type {
  PopularPlace,
  PopularPlaceDetail,
  PopularRoute,
  SearchPlaceDetail,
  SearchPlaceInformation,
} from '~models/Transportation/PointToPointTicket';
import type { Direction as DirectionType } from '~types/pointToPointTicket';

import SearchBar from './SearchType/Place/SearchBar';
import PlaceList from './SearchType/Place/PlaceList';
import NotFoundResult from './SearchType/Place/NotFoundResult';
import SuggestedPlace from './SearchType/Place/SuggestedPlace';

const { sendClickLog, sendLayerViewLog } = helper;

function generatePopularPlaceList(popularPlaceList: PopularPlace[]) {
  return popularPlaceList.map(({ placeTranslatedResult }) => placeTranslatedResult);
}

function generatePopularRouteList(
  popularRouteList: PopularRoute[],
): [PopularPlaceDetail, PopularPlaceDetail][] {
  return popularRouteList.map(({ origin, destination }) => [origin, destination]);
}

interface Props {
  direction: DirectionType;
  isOpen: boolean;
  onChangePlace: (direction: DirectionType, place: SearchPlaceInformation) => void;
  onClose: () => void;
}

const PlaceSearchSheet = ({ direction, isOpen, onChangePlace, onClose }: Props) => {
  const theme = useTheme();

  const searchBarRef = useRef<HTMLInputElement>(null);
  const isOrigin = direction === DIRECTION.ORIGIN;

  const baseLogData = {
    pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
    screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
  };

  const popularPlaceLogData = {
    ...baseLogData,
    eventName: isOrigin ? WeblogEventName.POPULAR_DEPARTURE : WeblogEventName.POPULAR_ARRIVAL,
  };

  const popularRouteLogData = {
    ...baseLogData,
    eventName: WeblogEventName.POPULAR_ROUTE,
  };

  const changePlace = (place: SearchPlaceInformation) => {
    onChangePlace(direction, place);
    onClose();
  };

  const handleSelectPlace = (place: SearchPlaceDetail) => {
    sendClickLog({
      ...baseLogData,
      eventName: isOrigin ? WeblogEventName.SELECT_DEPARTURE : WeblogEventName.SELECT_ARRIVAL,
      itemKind: 'button',
      itemName: isOrigin ? '출발지' : '도착지',
      ...(isOrigin ? { departure: place.content.korean } : { arrival: place.content.korean }),
    });

    changePlace({
      code: place.id,
      type: place.type,
      label: place.content.korean,
    });
  };

  const { query, selectedIndex, placeList, isResultNotFound, handleChangeInput, fetchNextPage } =
    usePlaceSearch({
      onSelectPlace: handleSelectPlace,
    });

  const { data: popularPlaceList } = usePopularPlaceList(
    direction === DIRECTION.ORIGIN ? 'ORIGIN' : 'DESTINATION',
  );
  const { data: popularRouteList } = usePopularRouteList();

  const handleAfterOpen = () => {
    searchBarRef.current?.focus();
  };

  const handleSelectSuggestedPlace = (place: PopularPlaceDetail) => {
    sendClickLog({
      ...popularPlaceLogData,
      itemKind: 'button',
      itemName: '지역명',
      ...(isOrigin ? { departure: place.koreanLabel } : { arrival: place.koreanLabel }),
    });

    changePlace({
      code: place.code,
      type: place.type,
      label: place.koreanLabel,
    });
  };

  const handleSelectSuggestedPlaces = (places: [PopularPlaceDetail, PopularPlaceDetail]) => {
    sendClickLog({
      ...popularRouteLogData,
      itemKind: 'button',
      itemName: '노선명',
      route: `${places[0].koreanLabel} - ${places[1].koreanLabel}`,
    });

    onChangePlace(DIRECTION.ORIGIN, {
      code: places[0].code,
      type: places[0].type,
      label: places[0].koreanLabel,
    });
    onChangePlace(DIRECTION.DESTINATION, {
      code: places[1].code,
      type: places[1].type,
      label: places[1].koreanLabel,
    });

    onClose();
  };

  const handleTouchScrollSection = () => {
    searchBarRef.current?.blur();
  };

  useEffect(() => {
    const logDataByDirection = {
      [DIRECTION.ORIGIN]: {
        eventName: WeblogEventName.DEPARTURE,
        itemName: '출발지 선택',
      },
      [DIRECTION.DESTINATION]: {
        eventName: WeblogEventName.ARRIVAL,
        itemName: '도착지 선택',
      },
    };

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: logDataByDirection[direction].eventName,
      itemName: logDataByDirection[direction].itemName,
    });
  }, [direction]);

  return (
    <BottomSheet
      isOpen={isOpen}
      detent="full"
      headerProps={{
        titleAlign: 'center',
        children: (
          <Text typography="headlineNormal17" color={theme.colors.gray.s1000} align="center" as="p">
            {direction === DIRECTION.ORIGIN ? '어디서 출발하세요?' : '어디로 떠나세요?'}
          </Text>
        ),
        closable: true,
        hasGrapple: true,
      }}
      backdropProps={{
        onClick: onClose,
      }}
      preventScrollProps={{ ignoreIosScroll: true }}
      contentProps={{
        className: css`
          transform: none !important;
        `,
        disableDrag: true,
      }}
      onOpened={handleAfterOpen}
      onXClick={onClose}
    >
      <SearchBarContainer>
        <SearchBar ref={searchBarRef} query={query} onChange={handleChangeInput} />
      </SearchBarContainer>
      <ScrollableSection onTouchStart={handleTouchScrollSection}>
        <GradientMask />
        {!query && (
          <SuggestedPlace
            sections={[
              {
                title: `많이 찾는 ${isOrigin ? '출발지' : '도착지'}`,
                places: popularPlaceList ? generatePopularPlaceList(popularPlaceList) : [],
              },
              {
                title: '인기 노선',
                notice: {
                  text: 'HOT',
                  backgroundColor: theme.colors.red.s400,
                  color: theme.colors.white,
                },
                places: popularRouteList ? generatePopularRouteList(popularRouteList) : [],
              },
            ]}
            onClickSinglePlace={handleSelectSuggestedPlace}
            onClickRoundPlace={handleSelectSuggestedPlaces}
          />
        )}
        {isResultNotFound && <NotFoundResult />}
        {query && !isResultNotFound && (
          <>
            <PlaceList
              searchKeyword={query}
              placeList={placeList ?? []}
              selectedIndex={selectedIndex}
              onPlaceClick={handleSelectPlace}
              onFetchMore={fetchNextPage}
            />
          </>
        )}
      </ScrollableSection>
    </BottomSheet>
  );
};

const SearchBarContainer = styled.div`
  background-color: ${({ theme: { palettes } }) => palettes.white};
  padding: 0 16px;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const GradientMask = styled.div`
  position: sticky;
  top: 48px;
  width: 100%;
  height: 24px;
  z-index: 2;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
`;

const ScrollableSection = styled.div``;

export default PlaceSearchSheet;
